import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import get from 'lodash/get';
import { Link } from 'react-router-dom';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';


import env from 'Env';
import 'Components/Product/ProductRelated/RelatedProductCard.scss';
import { countryCode } from 'Helpers/Geo/data';
import FluidImg from 'Components/Utility/FluidImg';
import Drm from 'Components/Utility/Drm';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import HitCardViewBtn from 'Components/Cards/HitCardViewBtn';
import StarRating from 'Components/ProductReviews/UserReviews/StarRating';
import DlcBadge from 'Components/Cards/DlcBadge';

export default function RelatedProductCard({ hit, headingTag, isCheckoutCard }) {
  const recommendedThreshold = env.percentRecommendedThreshold;
  const {
    name,
    cover,
    type,
    slug,
    drm,
    presale,
    free_to_play: freeToPlay,
    price,
    tiered,
    discount_percent: discountPercent,
    hide_discount: hideDiscount,
    fullPrice,
    pay_what_you_want: payWhatYouWant,
    product_id: productId,
    addVoucher,
    no_release_date: noReleaseDate,
    operating_systems: operatingSystems,
    sku,
  } = hit;

  let noGermanRelease = false;
  if (countryCode === 'DE' && noReleaseDate) {
    noGermanRelease = true;
  }

  const isPromotion = (type === 'pick-and-mix');

  let showAddButton = false;
  if (!hideDiscount && !isPromotion && !tiered
    && !presale && !noGermanRelease && !freeToPlay && !payWhatYouWant) {
    showAddButton = true;
  }

  const ratingStars = get(hit, 'rating_score', 0);
  const ratingTotal = get(hit, 'total_ratings', 0);
  const metacriticScore = get(hit, 'metacritic_score', 0);
  const opencriticScore = get(hit, 'opencritic_score', 0);

  const showMetacritic = metacriticScore >= recommendedThreshold;
  const showOpencritic = opencriticScore >= recommendedThreshold && !showMetacritic;

  const Tag = headingTag;

  return (
    <article className={classNames('related-product-card', { 'faux-block-link': !isCheckoutCard })} key={sku}>
      <div className="card-cover">
        <div className="responsive-image-island">
          <div className="responsive-image responsive-image--16by9">
            <FluidImg
              ratio16by9
              lazy
              className="img-force-full"
              src={cover}
              alt={name}
            />
          </div>
        </div>
      </div>
      <div className="card-content-container">
        <div className="name-icons-container">
          <Tag className="name-container">
            <Link className="faux-block-link__overlay-link" to={`/${type}/${slug}`} aria-label={`View ${hit.name}`}>
              {hit.name}
            </Link>
          </Tag>
          <div className="release-date">
            <FormattedMessage
              id="related-product-card.released-date" defaultMessage="Released:"
            />&nbsp;
            <FormattedDate value={new Date(hit.release_date * 1000)} year="numeric" month="long" day="2-digit" />
          </div>
          <div className="icons-container">
            {!isEmpty(drm) &&
              <Drm drmType={drm[0]} colorOverride={isCheckoutCard ? 'dark' : ''} />
            }
            <div className="divider" />
            {!isEmpty(operatingSystems) &&
              <div className="platforms-container">
                {operatingSystems && includes(operatingSystems, 'windows') &&
                  <FontAwesomeIcon icon={fabFaWindows} size="lg" />
                }
                {operatingSystems && includes(operatingSystems, 'mac') &&
                  <FontAwesomeIcon icon={fabFaApple} size="lg" />
                }
                {operatingSystems && includes(operatingSystems, 'linux') &&
                  <FontAwesomeIcon icon={fabFaLinux} size="lg" />
                }
              </div>
            }
            {hit.display_type === 'dlc' && !isCheckoutCard &&
              <React.Fragment>
                <div className="divider" />
                <DlcBadge />
              </React.Fragment>
            }
            {ratingStars > 0 &&
              <React.Fragment>
                <div className="divider" />
                <StarRating rating={ratingStars} />
                <span className="ml-2">
                  ({ratingTotal})
                </span>
              </React.Fragment>
            }
            {showMetacritic &&
              <React.Fragment>
                <div className="divider d-none d-sm-flex" />
                <div className="metacritic-container">
                  <div className="metacritic-score">
                    {metacriticScore}
                  </div>
                  <div className="metacritic-icon">
                    <img loading="lazy" src="https://cdn.fanatical.com/production/brand/metacritic/svg/metacritic-icon.svg" alt="Metacritic" />
                  </div>
                </div>
              </React.Fragment>
            }
            {showOpencritic &&
              <React.Fragment>
                <div className="divider d-none d-sm-flex" />
                <div className="metacritic-container">
                  <div className="metacritic-score">
                    {opencriticScore}
                  </div>
                  <div className="metacritic-icon">
                    <img loading="lazy" src="https://cdn.fanatical.com/production/brand/opencritic/svg/opencritic-icon.svg" alt="Opencrisic" />
                  </div>
                </div>
              </React.Fragment>
            }
          </div>
        </div>
        <div className="price-button-container">
          {!presale && !freeToPlay &&
            <PriceContainer
              discountPercent={discountPercent}
              price={price}
              tiered={tiered}
              hideDiscount={hideDiscount}
              slug={slug}
              type={type}
              fullPrice={fullPrice}
              payWhatYouWant={payWhatYouWant}
            />
          }
          <div className="button-container">
            {showAddButton &&
              <AddToCartBtn
                showInCartLink
                id={productId}
                slug={slug}
                voucherToAdd={addVoucher}
                place={'Related Product Card'}
                payWhatYouWant={hit.pay_what_you_want}
              />
            }
            {!showAddButton &&
              <HitCardViewBtn
                noFauxLink
                hit={hit}
                place={'Related Product Card'}
              />
            }
          </div>
        </div>
      </div>
    </article>
  );
}

RelatedProductCard.propTypes = {
  hit: PropTypes.shape({}),
  headingTag: PropTypes.string,
  isCheckoutCard: PropTypes.bool,
};

RelatedProductCard.defaultProps = {
  hit: {},
  headingTag: 'h3',
  isCheckoutCard: false,
};
